import { FIRST_LOAD_STATE_WAIT_MS } from 'projects/service/src/app/shared/utils/grid-persistence';
import { TemplateRef } from '@angular/core';
import { GridConfig } from '@inavista/components/grid';

// Shipment order popup → Receipts → Shipped Items on @inavista/components. Same columns, labels, order, widths,
// alignment, links (Item, Description) and toolbar (Columns, Search, Excel export for AllGrid_Toolbar_Excel_Export,
// Clear filters) as the Syncfusion grid it replaces. Rows are the popup's own receipts (LocalRowsGridApi). Serviceable
// Received Qty is edited inline with its editor (CellEditorHost), saved with the popup.

export const SHIPPED_ITEMS_URL = 'page://shipped-items';
export const SHIPPED_ITEMS_PERSISTENCE_KEY = 'rims.service.shipment-order-receipts-shipped-items';
/** A hidden field the page writes when a row opens, so leaving it always saves it (the Syncfusion grid saved on leave). */
export const SHIPPED_ITEMS_OPEN_FIELD = '__open';

export const SHIPPED_ITEMS_COLUMNS: [string, string][] = [
  ['tempRowId', 'int'],
  ['item', 'nvarchar'],
  ['itemDescription', 'nvarchar'],
  ['orderQty', 'decimal'],
  ['quantityShippedInInventoryUnit', 'decimal'],
  ['quantityServiceable', 'decimal'],
  ['shortShipment', 'decimal'],
  ['overShipment', 'decimal'],
  [SHIPPED_ITEMS_OPEN_FIELD, 'nvarchar'],
];

const SHOWN = SHIPPED_ITEMS_COLUMNS.map(([c]) => c).filter(c => c !== 'tempRowId' && c !== SHIPPED_ITEMS_OPEN_FIELD);
const RIGHT = ['orderQty', 'quantityShippedInInventoryUnit', 'quantityServiceable', 'shortShipment', 'overShipment'];

export function buildShippedItemsGridConfig(canExport: boolean, qtyEditor: TemplateRef<unknown>, canEdit: () => boolean): GridConfig {
  return {
    apiBaseUrl: SHIPPED_ITEMS_URL,
    clientSide: true,
    keyColumn: 'tempRowId',
    rowHeight: 30,
    defaultColumnOrder: SHOWN,
    hiddenByDefaultColumns: ['tempRowId', SHIPPED_ITEMS_OPEN_FIELD],
    // Serviceable Received Qty and Over Shipped were out of the column chooser.
    alwaysVisibleColumns: ['quantityServiceable', 'overShipment'],
    columnLabels: {
      item: 'Item', itemDescription: 'Description', orderQty: 'Ord Qty', quantityShippedInInventoryUnit: 'Shipped Qty',
      quantityServiceable: 'Serviceable Received Qty', shortShipment: 'Short Shipped', overShipment: 'Over Shipped',
    },
    // The old alignment: quantities right, the rest left; headers left (headerTextAlign 'left').
    columnAlign: Object.fromEntries(SHOWN.map(c => [c, {
      horizontal: RIGHT.includes(c) ? 'right' as const : 'left' as const, header: { horizontal: 'left' as const },
    }])),
    // The old widths; Item and Description had none and shared the rest.
    columnWidths: {
      item: 150, itemDescription: 200, orderQty: 157, quantityShippedInInventoryUnit: 157, quantityServiceable: 142,
      shortShipment: 157, overShipment: 157,
    },
    stretchColumns: true,
    columnSizing: Object.fromEntries(RIGHT.map(c => [c, { noStretch: true }])),
    // Syncfusion's 'n': grouped, up to 3 decimals.
    columnNumberFormats: Object.fromEntries(RIGHT.map(c => [c, { maximumFractionDigits: 3 }])),
    hyperlinkColumns: ['item', 'itemDescription'],
    // Rows open only for a user who may change them, as the old editSettings.allowEditing (the page rebuilds
    // the config when that changes): a row whose every cell is locked would still open, with nothing to edit.
    editMode: canEdit() ? 'inline' : 'none',
    editSaveMode: 'row',
    // A double click opens the row, as the Syncfusion grid.
    editTrigger: 'doubleClick',
    editableColumns: ['quantityServiceable'],
    editorTemplates: { quantityServiceable: qtyEditor },
    // Receipts are edited while the order is Delivery Acknowledged, with Inv_Order_Receipt_Popup_Receipt_Tab edit.
    readOnlyCellFn: () => !canEdit(),
    deleteMode: 'host',
    rowDeletable: () => false,
    enableColumnsPicker: true,
    enableExport: canExport,
    enableSavedViews: false,
    enableScrollButtons: false,
    enableFitColumns: false,
    enableResetLayout: true,
    enableRefresh: false,
    enableFooter: false,
    multiSort: true,
    emptyStateMessage: 'No records to display',
    // Right-click a cell filters by its value (Martin, 2026-10-29: every grid that filters).
    enableContextMenuFilter: true,
    persistenceKey: SHIPPED_ITEMS_PERSISTENCE_KEY,
    firstLoadStateWaitMs: FIRST_LOAD_STATE_WAIT_MS,
  };
}

